import { initializeApp } from "firebase/app";
import { Capacitor } from '@capacitor/core';
import { getAuth, GoogleAuthProvider, connectAuthEmulator, getIdToken, initializeAuth, indexedDBLocalPersistence, onIdTokenChanged, browserLocalPersistence } from "firebase/auth";
import { getDataConnect, connectDataConnectEmulator } from "firebase/data-connect";
import { connectorConfig } from "@firebasegen/default-connector";
import { initializeAppCheck, ReCaptchaV3Provider, CustomProvider } from "firebase/app-check";
import { FirebaseAppCheck } from "@capacitor-firebase/app-check";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage } from "firebase/storage";
import { getFirestore } from "firebase/firestore";
import Logger from "./utils/Logger";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const recapta_key = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export const app = initializeApp(firebaseConfig);

// Initialize App Check
let appCheck;
if (Capacitor.getPlatform() === 'web') {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if ((recapta_key || isLocal) && !appCheck) {
    Logger.info("FirebaseConfig", "Initializing App Check (Web)", { isLocal });
    
    if (isLocal) {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || true;
    }

    try {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(recapta_key || '6Lc77p0pAAAAANX_8_8_8_8_8_8_8_8_8_8_8_8'), // Bruker en standard dummy-nøkkel som fungerer med debug-modus
        isTokenAutoRefreshEnabled: true
      });
    } catch (err) {
      if (err.code === 'app-check/already-initialized') {
         Logger.info("FirebaseConfig", "App Check already initialized, skipping.");
      } else {
         Logger.error("FirebaseConfig", "App Check initialization failed", err);
      }
    }
  }
} else {
  // Use Capacitor Plugin for Native App Check (App Attest / DeviceCheck)
  const isDev = process.env.NODE_ENV !== 'production';
  Logger.info("FirebaseConfig", "Initializing App Check (Native)", { isDev });

  FirebaseAppCheck.initialize({
    debugToken: isDev
  }).catch(err => Logger.error("FirebaseConfig", "Failed to initialize FirebaseAppCheck plugin", err));

  appCheck = initializeAppCheck(app, {
    provider: new CustomProvider({
      getToken: async () => {
        try {
          Logger.debug("FirebaseConfig", "AppCheck CustomProvider: Fetching token from plugin...");
          const { token, expireTimeMillis } = await FirebaseAppCheck.getToken();

          if (!token) {
            Logger.warn("FirebaseConfig", "AppCheck CustomProvider: Plugin returned empty token");
          } else {
            Logger.info("FirebaseConfig", "AppCheck CustomProvider: Successfully received token", {
              expiresIn: expireTimeMillis ? (expireTimeMillis - Date.now()) / 1000 : 'unknown',
              tokenPrefix: token.substring(0, 10) + '...'
            });
          }

          return {
            token: token,
            expireTimeMillis: expireTimeMillis || (Date.now() + 60 * 60 * 1000),
          };
        } catch (error) {
          Logger.error("FirebaseConfig", "AppCheck CustomProvider: Error getting token from plugin", error);
          throw error;
        }
      }
    }),
    isTokenAutoRefreshEnabled: true
  });
}
export { appCheck };

// Initialize Auth with persistence suitable for the platform
let auth;
if (Capacitor.getPlatform() === 'web') {
  auth = getAuth(app);
} else {
  // For native, initializeAuth with indexedDBLocalPersistence is more reliable
  Logger.info("FirebaseConfig", "Initializing Auth with indexedDBLocalPersistence");
  auth = initializeAuth(app, {
    persistence: indexedDBLocalPersistence
  });
}

export { auth };
export const googleProvider = new GoogleAuthProvider();

export const db = getFirestore(app);


export const getHeaders = async () => {
  const user = auth.currentUser;
  if (!user) {
    Logger.warn("FirebaseConfig", "getHeaders called but user is not logged in");
    throw new Error("User is not logged in");
  }
  try {
    const token = await getIdToken(user);
    return { headers: { Authorization: `Bearer ${token}` } };
  } catch (error) {
    Logger.error("FirebaseConfig", "Failed to get ID token", error);
    throw error;
  }
};

export const getMe = () => {
  const user = auth.currentUser;
  if (user) {
    return user;
  } else {
    return null;
  }
};

export const GoogleMapsAPIKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;


export const dataConnect = getDataConnect(app, connectorConfig, { auth });

// Disable @firebase/data-connect's internal never-expiring subscriptionCache fallback
// so React Query's invalidateQueries/refetch always fetches fresh data from the server.
if (typeof dataConnect.setInitialized === 'function') {
  dataConnect.setInitialized();
}
if (dataConnect._queryManager) {
  dataConnect._queryManager.getFromSubscriberCache = async () => undefined;
}

// Ensure Data Connect gets the token updates
onIdTokenChanged(auth, async (user) => {
  if (user) {
    Logger.info("FirebaseConfig", "Auth token changed, user is logged in", {
      uid: user.uid,
      email: user.email,
      isAnonymous: user.isAnonymous
    });
    // Log token existence (don't log the full token for security)
    user.getIdToken().then(token => {
      Logger.debug("FirebaseConfig", "Auth token retrieved", { tokenPrefix: token.substring(0, 10) + '...' });
    }).catch(err => {
      Logger.error("FirebaseConfig", "Auth onIdTokenChanged: Failed to get token", err);
    });
  } else {
    Logger.info("FirebaseConfig", "Auth token changed, user is logged out");
    localStorage.removeItem('googleAccessToken');
  }
});

export const storage = getStorage(app);


export const functions = getFunctions(app);